/**
 * <EggPools> — Admin → Egg Pools.
 *
 * Every egg handed out on the site (raid prizes, shop, kits, Give) rolls a
 * pal from these pools; PalDefender needs that PalID or the egg never
 * hatches. This page previews what each egg can hatch and lets an admin
 * change it: pals in/out, level range, reset to the shipped default, plus a
 * dry-run roller and a "where is this egg used" panel.
 */
import { useEffect, useMemo, useState } from "react";
import { Egg, Search, X, Plus, RotateCcw, Dices, MapPin, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Loader } from "@/components/Atoms";

export function EggPools() {
  const [eggs, setEggs] = useState([]);
  const [palOptions, setPalOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    api.get("/admin/egg-pools")
      .then(({ data }) => {
        setEggs(data.eggs || []);
        setSelected((data.eggs || [])[0]?.id || null);
      })
      .catch((e) => toast.error(apiError(e, "Failed to load egg pools")))
      .finally(() => setLoading(false));
    // Fetched ONCE for the whole page — 934 pals, shared by every egg
    // editor (re-fetching per egg silently left the add-pal list empty).
    api.get("/admin/egg-pools/pal-options")
      .then(({ data }) => setPalOptions(data?.pals || []))
      .catch(() => setPalOptions([]));
  }, []);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return eggs;
    return eggs.filter((e) => e.name.toLowerCase().includes(q) || e.id.toLowerCase().includes(q));
  }, [eggs, query]);

  const markEdited = (id, patch) =>
    setEggs((list) => list.map((e) => (e.id === id ? { ...e, ...patch } : e)));

  if (loading) return <Loader />;

  return (
    <div className="fade-up space-y-3" data-testid="admin-egg-pools">
      <div className="glass-card p-3">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Egg size={14} className="text-amber-300" /> Egg hatch pools
        </h2>
        <p className="text-xs text-[var(--muted)] mt-1 leading-snug">
          What each egg can hatch, anywhere it's obtained — raid prizes, shop,
          kits and Give. Edits save to the shared database and apply instantly
          on the site. Levels are what the pal hatches at.
        </p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-3">
        <div className="glass-card p-2">
          <div className="flex items-center gap-1.5 mb-2">
            <Search size={12} className="text-[var(--muted)]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${eggs.length} eggs…`}
              className="input input-sm flex-1 text-xs"
              data-testid="egg-pools-search"
            />
          </div>
          <ul className="max-h-[70vh] overflow-y-auto space-y-0.5">
            {shown.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => setSelected(e.id)}
                  className={`w-full text-left flex items-center gap-2 px-1.5 py-1 rounded transition-colors ${
                    selected === e.id ? "bg-white/10" : "hover:bg-white/5"
                  }`}
                  data-testid={`egg-pools-item-${e.id}`}
                >
                  <img src={e.image} alt="" className="w-6 h-6 object-contain shrink-0" loading="lazy" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] text-[var(--bone)] truncate">
                      {e.name} {e.edited && <span className="text-amber-300">•</span>}
                    </span>
                    <span className="block text-[9px] text-[var(--muted)] truncate">
                      {e.pal_count} pals · Lv {e.level[0]}-{e.level[1]}
                    </span>
                  </span>
                </button>
              </li>
            ))}
            {shown.length === 0 && (
              <li className="text-xs text-[var(--muted)] px-2 py-3">No match.</li>
            )}
          </ul>
        </div>

        {selected
          ? <EggEditor key={selected} eggId={selected} options={palOptions} onSaved={markEdited} />
          : <div className="glass-card p-4 text-xs text-[var(--muted)]">Pick an egg.</div>}
      </div>
    </div>
  );
}

function EggEditor({ eggId, options = [], onSaved }) {
  const [egg, setEgg] = useState(null);
  const [level, setLevel] = useState([1, 1]);
  const [pals, setPals] = useState([]);       // [{id,name,image,valid}]
  const [addQuery, setAddQuery] = useState("");
  const [rolls, setRolls] = useState(null);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/egg-pools/${eggId}`)
      .then(({ data }) => {
        if (cancelled) return;
        setEgg(data);
        setPals(data.pals || []);
        setLevel(data.level || [1, 1]);
      })
      .catch((e) => { if (!cancelled) toast.error(apiError(e, "Failed to load egg")); });
    // Usage is nice-to-have — a failure here must not block the editor.
    api.get(`/admin/egg-pools/${eggId}/usage`)
      .then(({ data }) => { if (!cancelled) setUsage(data || null); })
      .catch(() => { if (!cancelled) setUsage(null); });
    return () => { cancelled = true; };
  }, [eggId]);

  const inPool = useMemo(() => new Set(pals.map((p) => p.id)), [pals]);
  const addResults = useMemo(() => {
    const q = addQuery.trim().toLowerCase();
    if (!q) return [];
    return options
      .filter((o) => !inPool.has(o.id))
      .filter((o) => o.name.toLowerCase().includes(q) || o.id.toLowerCase().includes(q))
      .slice(0, 30);
  }, [options, addQuery, inPool]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/egg-pools/${eggId}`, {
        pals: pals.map((p) => p.id),
        level: [Number(level[0]) || 1, Number(level[1]) || 1],
      });
      setEgg(data); setPals(data.pals); setLevel(data.level);
      onSaved?.(eggId, { pal_count: data.pals.length, level: data.level, edited: true });
      setRolls(null);
      toast.success("Egg pool saved — live everywhere on the site");
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm(`Reset ${egg?.name} back to its shipped pool?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/egg-pools/${eggId}/reset`);
      setEgg(data); setPals(data.pals); setLevel(data.level);
      onSaved?.(eggId, { pal_count: data.pals.length, level: data.level, edited: false });
      setRolls(null);
      toast.success("Reset to default");
    } catch (e) {
      toast.error(apiError(e, "Reset failed"));
    } finally { setBusy(false); }
  };

  const testRoll = async () => {
    try {
      const { data } = await api.post(`/admin/egg-pools/${eggId}/test-roll?count=10`);
      setRolls(data);
    } catch (e) { toast.error(apiError(e, "Roll failed")); }
  };

  if (!egg) return <div className="glass-card p-4"><Loader /></div>;

  const dirty =
    pals.length !== (egg.pals || []).length ||
    pals.some((p, i) => p.id !== egg.pals[i]?.id) ||
    Number(level[0]) !== egg.level[0] || Number(level[1]) !== egg.level[1];

  return (
    <div className="glass-card p-4 space-y-4" data-testid={`egg-pool-editor-${eggId}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <img src={egg.image} alt="" className="w-10 h-10 object-contain" />
        <div className="min-w-[180px] flex-1">
          <h3 className="text-sm font-semibold truncate">{egg.name}</h3>
          <p className="text-[10px] font-mono text-[var(--muted)]">{egg.id}</p>
          <p className="text-[11px] text-[var(--muted)] mt-0.5 whitespace-nowrap">
            {pals.length} pals
            {egg.edited && <span className="text-amber-300"> · customised</span>}
            {!egg.edited && <span> · shipped default</span>}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <label className="text-[10px] text-[var(--muted)] flex items-center gap-1">
            Lv
            <input
              type="number" min={1} max={255}
              value={level[0]}
              onChange={(e) => setLevel([e.target.value, level[1]])}
              className="input input-sm" style={{ width: 62 }}
              data-testid="egg-pool-level-min"
            />
            –
            <input
              type="number" min={1} max={255}
              value={level[1]}
              onChange={(e) => setLevel([level[0], e.target.value])}
              className="input input-sm" style={{ width: 62 }}
              data-testid="egg-pool-level-max"
            />
          </label>
          <button type="button" onClick={testRoll} className="btn btn-ghost text-xs flex items-center gap-1"
                  data-testid="egg-pool-test-roll">
            <Dices size={12} /> Test roll
          </button>
          <button type="button" onClick={reset} disabled={busy || !egg.edited}
                  className="btn btn-ghost text-xs flex items-center gap-1" data-testid="egg-pool-reset">
            <RotateCcw size={12} /> Reset
          </button>
          <button type="button" onClick={save} disabled={busy || !dirty || pals.length === 0}
                  className="btn btn-cta text-xs" data-testid="egg-pool-save">
            {busy ? "Saving…" : dirty ? "Save pool" : "Saved"}
          </button>
        </div>
      </div>

      {/* Add a pal */}
      <div className="relative">
        <div className="flex items-center gap-1.5">
          <Plus size={12} className="text-[var(--teal)]" />
          <input
            value={addQuery}
            onChange={(e) => setAddQuery(e.target.value)}
            placeholder={`Add a pal to this egg (search ${options.length})…`}
            className="input input-sm flex-1 text-xs"
            data-testid="egg-pool-add-search"
          />
        </div>
        {addResults.length > 0 && (
          <ul className="absolute z-30 mt-1 w-[320px] max-w-[calc(100vw-32px)] max-h-60 overflow-y-auto rounded-md border border-white/10 bg-[#11131a] shadow-xl p-1.5 space-y-0.5">
            {addResults.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  onClick={() => { setPals((p) => [...p, { ...o, valid: true }]); setAddQuery(""); }}
                  className="w-full text-left flex items-center gap-2 px-1.5 py-1 rounded hover:bg-white/5"
                  data-testid={`egg-pool-add-option-${o.id}`}
                >
                  {o.image
                    ? <img src={o.image} alt="" className="w-6 h-6 object-contain shrink-0" loading="lazy" />
                    : <span className="w-6 h-6 shrink-0" />}
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] text-[var(--bone)] truncate">{o.name}</span>
                    <span className="block text-[9px] font-mono text-[var(--muted)] truncate">{o.id}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Current pool */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="egg-pool-pals">
        {pals.map((p) => (
          <div key={p.id}
               className="flex items-center gap-2 rounded border border-white/10 p-1.5 bg-white/[0.02]"
               data-testid={`egg-pool-pal-${p.id}`}>
            {p.image
              ? <img src={p.image} alt="" className="w-8 h-8 object-contain shrink-0" loading="lazy" />
              : <span className="w-8 h-8 shrink-0 grid place-items-center text-[10px] text-[var(--muted)]">?</span>}
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] text-[var(--bone)] truncate">{p.name}</span>
              <span className="block text-[9px] font-mono text-[var(--muted)] truncate">{p.id}</span>
            </span>
            {!p.valid && <span className="text-[9px] text-[var(--error)]">unknown</span>}
            <button type="button"
                    onClick={() => setPals((list) => list.filter((x) => x.id !== p.id))}
                    className="text-[var(--muted)] hover:text-[var(--error)]"
                    aria-label={`Remove ${p.name}`}
                    data-testid={`egg-pool-remove-${p.id}`}>
              <X size={12} />
            </button>
          </div>
        ))}
        {pals.length === 0 && (
          <p className="text-xs text-[var(--error)] col-span-full">
            An egg with no pals never hatches — add at least one before saving.
          </p>
        )}
      </div>

      {/* Test roll output */}
      {rolls && (
        <div className="rounded border border-white/10 p-2.5" data-testid="egg-pool-roll-results">
          <p className="text-[10px] uppercase tracking-wider text-[var(--muted)] mb-1.5">
            10 dry-run rolls (nothing sent to the server)
          </p>
          <div className="flex flex-wrap gap-1.5">
            {rolls.rolls.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full border border-white/10 flex items-center gap-1">
                {r.image && <img src={r.image} alt="" className="w-3.5 h-3.5 object-contain" />}
                {r.name} <span className="text-[var(--muted)]">Lv {r.Level}</span>
              </span>
            ))}
          </div>
          <p className="text-[10px] text-[var(--muted)] mt-1.5">
            {rolls.tally.map((t) => `${t.pal} ×${t.count}`).join(" · ")}
          </p>
        </div>
      )}

      {/* Where used */}
      {usage && (
        <div className="rounded border border-white/10 p-2.5" data-testid="egg-pool-usage">
          <p className="text-[10px] uppercase tracking-wider text-[var(--muted)] mb-1.5 flex items-center gap-1">
            <MapPin size={11} /> Where this egg comes from
          </p>
          {usage.shop.length === 0 && usage.kits.length === 0 && usage.raid_prizes.length === 0 ? (
            <p className="text-[11px] text-[var(--muted)]">
              Not in the shop, any kit, or the raid prize table right now.
            </p>
          ) : (
            <ul className="text-[11px] space-y-0.5">
              {usage.shop.map((s) => (
                <li key={`s-${s.name}`} className="flex items-center gap-1.5">
                  <Check size={10} className="text-[var(--teal)]" /> Shop · {s.name}
                  {s.price != null && <span className="text-[var(--muted)]">({s.price} coins)</span>}
                </li>
              ))}
              {usage.kits.map((k) => (
                <li key={`k-${k.name}`} className="flex items-center gap-1.5">
                  <Check size={10} className="text-[var(--teal)]" /> Kit · {k.name}
                </li>
              ))}
              {usage.raid_prizes.map((r) => (
                <li key={`r-${r.rank}`} className="flex items-center gap-1.5">
                  <Check size={10} className="text-[var(--teal)]" /> Raid prize · rank {r.rank} ×{r.count}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
